// sensei-app/client/ui/ClassroomTurnManager.js
import { ComponentBase } from "../../core/ComponentBase.js";

export class ClassroomTurnManager extends ComponentBase {
    static styles = `
        .turn-manager-container {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.75rem;
            gap: 1rem;
            background-color: var(--bs-tertiary-bg);
            border-top: 1px solid var(--bs-border-color);
            flex-shrink: 0;
            transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .turn-manager-container.hidden {
            opacity: 0;
            visibility: hidden;
            height: 0;
            padding: 0;
            overflow: hidden;
        }
        .progress-circle-container {
            position: relative;
            width: 40px;
            height: 40px;
        }
        .progress-circle-svg {
            transform: rotate(-90deg);
        }
        .progress-circle-bg {
            fill: none;
            stroke: var(--bs-border-color-translucent);
            stroke-width: 4;
        }
        .progress-circle-fg {
            fill: none;
            stroke: var(--bs-primary);
            stroke-width: 4;
            stroke-linecap: round;
            transition: stroke-dashoffset 0.1s linear;
        }
        .progress-circle-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 0.8rem;
            font-weight: 500;
            color: var(--bs-emphasis-color);
        }
    `;

    constructor(config) {
        super(config);
        this.timerInterval = null;
        this.duration = 30; // Default duration in seconds
        this.remaining = 30;
        this.isPaused = false;
        this.applyStyles();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'turn-manager-container hidden';
            // NEW: Added a wrapper for the active controls and a new div for the processing state
            this.container.innerHTML = `
            <div class="turn-manager-active-controls d-flex align-items-center justify-content-center gap-3">
                <button class="btn btn-sm btn-outline-secondary pause-resume-btn" title="${this.t('Pause Timer')}"><i class="bi bi-pause-fill"></i></button>
                <button class="btn btn-primary next-turn-btn">
                    <i class="bi bi-arrow-right-circle-fill me-2"></i>
                    <span data-i18n="Next">Next</span>
                </button>
                <div class="progress-circle-container">
                    <svg class="progress-circle-svg" width="40" height="40" viewBox="0 0 40 40">
                        <circle class="progress-circle-bg" cx="20" cy="20" r="18"></circle>
                        <circle class="progress-circle-fg" cx="20" cy="20" r="18"></circle>
                    </svg>
                    <div class="progress-circle-text">30s</div>
                </div>
            </div>
            <div class="turn-manager-processing-indicator d-none align-items-center gap-2 text-muted">
                <div class="spinner-border spinner-border-sm" role="status"></div>
                <span data-i18n="AI is thinking...">AI is thinking...</span>
            </div>
        `;
            this.activeControls = this.container.querySelector('.turn-manager-active-controls');
            this.processingIndicator = this.container.querySelector('.turn-manager-processing-indicator');
            this.progressCircleFg = this.container.querySelector('.progress-circle-fg');
            this.progressText = this.container.querySelector('.progress-circle-text');
            this.pauseResumeBtn = this.container.querySelector('.pause-resume-btn');

            this.container.querySelector('.next-turn-btn').addEventListener('click', () => this.advance());
            this.pauseResumeBtn.addEventListener('click', () => this.togglePause());
        }
        return this.container;
    }

    // Drop-in replacement for the start method
    start(duration = 30) {
        this.stop(); // Ensure any previous state is cleared
        this.duration = duration;
        this.remaining = duration;
        this.isPaused = false;

        // Show active controls, hide processing indicator
        this.activeControls.classList.remove('d-none');
        this.activeControls.classList.add('d-flex');
        this.processingIndicator.classList.add('d-none');

        this.container.classList.remove('hidden');
        this.updateProgress();
        this.pauseResumeBtn.innerHTML = '<i class="bi bi-pause-fill"></i>';

        this.timerInterval = setInterval(() => {
            if (this.isPaused) return;
            this.remaining--;
            this.updateProgress();
            if (this.remaining <= 0) {
                this.advance();
            }
        }, 1000);
    }

    // Drop-in replacement for the advance method
    advance() {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
        this.showProcessing(); // NEW: Show processing state instead of stopping
        this.emit('advanceTurn');
    }

    // NEW METHOD: Add this to ClassroomTurnManager.js
    showProcessing() {
        this.container.classList.remove('hidden');
        this.activeControls.classList.add('d-none');
        this.activeControls.classList.remove('d-flex');
        this.processingIndicator.classList.remove('d-none');
        this.processingIndicator.classList.add('d-flex');
    }


    stop() {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
        this.container.classList.add('hidden');
    }

    togglePause() {
        this.isPaused = !this.isPaused;
        this.pauseResumeBtn.innerHTML = this.isPaused ? '<i class="bi bi-play-fill"></i>' : '<i class="bi bi-pause-fill"></i>';
        this.pauseResumeBtn.title = this.isPaused ? this.t('Resume Timer') : this.t('Pause Timer');
    }

    updateProgress() {
        const radius = this.progressCircleFg.r.baseVal.value;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (this.remaining / this.duration) * circumference;
        this.progressCircleFg.style.strokeDasharray = circumference;
        this.progressCircleFg.style.strokeDashoffset = offset;
        this.progressText.textContent = `${this.remaining}s`;
    }
}